/**
 * Where the page is actually running — the question behind "why did my friend's
 * tap open the browser and not the app I installed?".
 *
 * A shared link (see share.ts) usually arrives through WhatsApp, and WhatsApp
 * renders it in its OWN embedded WebView. That WebView never goes through
 * Android's intent resolver, so the installed WebAPK is never even offered the
 * URL — no manifest member can change that. The only way out is to hand the user
 * an explicit escape (see androidIntentUrl below).
 *
 * On iOS there is no way out at all: home-screen web apps cannot capture links,
 * and the intent: scheme does not exist. 'in-app-ios' is reported so the UI can
 * say something true rather than promise an app hand-off that cannot happen.
 */
export type BrowserContext = 'standalone' | 'in-app-android' | 'in-app-ios' | 'browser';

/** Running inside the installed PWA (either platform's flavour of "standalone"). */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia?.('(display-mode: standalone)').matches) return true;
  // iOS home-screen web apps: non-standard, Safari-only, and the ONLY signal there.
  return (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
}

/**
 * Android WebViews all carry the `; wv)` token in their UA, which covers WhatsApp,
 * Instagram, Facebook, Telegram and the rest in one check — no per-app list to keep
 * up to date.
 */
const ANDROID_WEBVIEW = /;\s*wv\)/;

/** iOS in-app browsers that announce themselves. Chrome/Firefox on iOS are NOT here:
 *  they are real browsers the user chose, not a container they got trapped in. */
const IOS_IN_APP = /\b(FBAN|FBAV|FB_IAV|Instagram|Line|Twitter|Snapchat|Pinterest|LinkedInApp)\b/;

const IOS_PLATFORM = /iPhone|iPad|iPod/;

/**
 * @param ua defaults to the live user agent; injectable so the whole thing stays a
 * pure function and can be tested without a browser.
 */
export function detectBrowserContext(ua?: string): BrowserContext {
  // Inside the installed app there is nothing to escape from, whatever the UA says.
  if (isStandalone()) return 'standalone';

  const agent = ua ?? (typeof navigator !== 'undefined' ? navigator.userAgent : '');
  if (!agent) return 'browser';

  if (ANDROID_WEBVIEW.test(agent)) return 'in-app-android';
  if (IOS_PLATFORM.test(agent) && IOS_IN_APP.test(agent)) return 'in-app-ios';

  /*
   * WhatsApp on iOS is the case that matters most here and it identifies itself
   * with NOTHING — no app token, and a UA close enough to Safari's that there is no
   * honest positive test. It is caught the other way round: an iOS WebView driven by
   * an app reports the WebKit build without the "Safari/<version>" token that real
   * Safari always appends.
   *
   * Deliberately conservative everywhere else: anything ambiguous is reported as
   * 'browser', because a banner shown to someone who is already in a real browser is
   * a worse bug than a banner missed.
   */
  if (IOS_PLATFORM.test(agent) && /AppleWebKit/.test(agent) && !/Safari\//.test(agent)) {
    return 'in-app-ios';
  }

  return 'browser';
}

/**
 * The escape hatch: an intent: URL makes Android's WebView give up the navigation
 * and hand it to the system resolver, which routes it to the installed WebAPK (or,
 * via browser_fallback_url, to the default browser when there is no app).
 *
 * Android only — there is no iOS equivalent, by design of the platform.
 *
 * @param url the FULL url to reopen, query string included. Pass the URL captured on
 * first render, never `location.href`: App.tsx strips the deep-link query off the
 * address bar on mount, so the live URL has already lost the payload and the app
 * would reopen on the home page instead of the shared challenge.
 */
export function androidIntentUrl(url: string): string {
  const { host, pathname, search } = new URL(url);
  return (
    `intent://${host}${pathname}${search}` +
    '#Intent;scheme=https;action=android.intent.action.VIEW;' +
    'category=android.intent.category.BROWSABLE;' +
    `S.browser_fallback_url=${encodeURIComponent(url)};end`
  );
}

/**
 * Quale sistema operativo, per scegliere le istruzioni d'installazione giuste.
 *
 * Volutamente a grana grossa: le tre risposte della FAQ sono Android, iPhone/iPad
 * e computer, quindi qualunque cosa non sia telefono o tablet finisce in
 * 'desktop'. `ua` iniettabile come in detectBrowserContext, così resta pura.
 */
export function detectPlatform(ua?: string): 'android' | 'ios' | 'desktop' {
  const agent = ua ?? (typeof navigator !== 'undefined' ? navigator.userAgent : '');
  if (/Android/.test(agent)) return 'android';
  if (IOS_PLATFORM.test(agent)) return 'ios';
  /*
   * L'iPad dal 2019 si annuncia come un Mac e non compare in IOS_PLATFORM. Lo si
   * riconosce dall'unico Mac che ha il touch: `maxTouchPoints > 1`. Se il UA e'
   * stato passato a mano (test) il controllo non si puo' fare e resta 'desktop',
   * che e' la risposta prudente.
   */
  if (
    ua === undefined &&
    /Macintosh/.test(agent) &&
    typeof navigator !== 'undefined' &&
    navigator.maxTouchPoints > 1
  ) {
    return 'ios';
  }
  return 'desktop';
}
